import type { User } from "hooks/useAddUpdateUser";

// An owner keeps their id when their email is edited; only one added in this
// session has no id yet, and is matched by email until it gets one.
export const isSameProfile = (a: User, b: User): boolean =>
  a.id && b.id
    ? a.id === b.id
    : Boolean(a.email) &&
      a.email.toLowerCase() === (b.email ?? "").toLowerCase();

export const mergeUserProfiles = (
  existing: User[],
  incoming: User | User[],
): User[] => {
  const merged = [...existing];
  (Array.isArray(incoming) ? incoming : [incoming]).forEach((user) => {
    const index = merged.findIndex((profile) => isSameProfile(profile, user));
    if (index === -1) merged.push(user);
    else merged[index] = { ...merged[index], ...user };
  });
  return merged;
};

// Follows the current answer, since activateCompany sends every submitted user
// whose stored isOwner is true to the issuer as a beneficial owner.
export const beneficialOwnersFor = (
  profiles: User[],
  ownersRequired: boolean,
): User[] =>
  ownersRequired ? profiles.filter((profile) => profile.isOwner) : [];

export const profilesToSubmit = (
  authorizedPerson: User | undefined,
  beneficialOwners: User[],
): User[] =>
  [authorizedPerson, ...beneficialOwners]
    .filter((profile): profile is User => Boolean(profile))
    .filter(
      (profile, index, all) =>
        all.findIndex((other) => isSameProfile(other, profile)) === index,
    );
